import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface GeminiBottomSheetProps {
  open: boolean;
  onClose: () => void;
  query: string;
  transcript?: string;
  isListening?: boolean;
}

interface StreamOptions {
  query: string;
  signal: AbortSignal;
  onDelta: (delta: string) => void;
}

const StreamingText = ({ text, loading }: { text: string; loading: boolean }) => (
  <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
    {text}
    {loading && (
      <motion.span
        animate={{ opacity: [1, 0] }}
        transition={{ repeat: Infinity, duration: 0.7 }}
        className="ml-0.5 inline-block h-4 w-0.5 align-text-bottom bg-primary"
      />
    )}
  </p>
);

async function streamAIResponse({ query, signal, onDelta }: StreamOptions): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return "Devi effettuare il login.";

  const resp = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`,
    {
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        messages: [{ role: "user", content: query }],
        skipMemory: true,
      }),
    }
  );

  if (resp.status === 429) return "Troppi tentativi in questo momento. Riprova tra poco.";
  if (resp.status === 402) return "Il servizio AI non è disponibile ora. Riprova più tardi.";
  if (!resp.ok || !resp.body) return "Errore nella generazione della risposta.";

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    let newlineIndex = buffer.indexOf("\n");
    while (newlineIndex !== -1) {
      let line = buffer.slice(0, newlineIndex);
      buffer = buffer.slice(newlineIndex + 1);

      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (!line.startsWith("data: ")) {
        newlineIndex = buffer.indexOf("\n");
        continue;
      }

      const payload = line.slice(6).trim();
      if (!payload || payload === "[DONE]") {
        newlineIndex = buffer.indexOf("\n");
        continue;
      }

      try {
        const parsed = JSON.parse(payload);
        const content = parsed.choices?.[0]?.delta?.content;
        if (content) onDelta(content);
      } catch {
        buffer = `${line}\n${buffer}`;
        break;
      }

      newlineIndex = buffer.indexOf("\n");
    }
  }

  return null;
}

export const GeminiBottomSheet = ({
  open,
  onClose,
  query,
  transcript = "",
  isListening = false,
}: GeminiBottomSheetProps) => {
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const streamedQueryRef = useRef("");
  const abortRef = useRef<AbortController | null>(null);

  // Reset internal state when sheet closes
  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      abortRef.current = null;
      streamedQueryRef.current = "";
      setResponse("");
      setErrorMessage("");
      setLoading(false);
    }
  }, [open]);

  // Trigger AI streaming when we have a query and are no longer listening
  const startStream = useCallback((q: string) => {
    if (!q || streamedQueryRef.current === q) return;
    streamedQueryRef.current = q;

    setResponse("");
    setErrorMessage("");
    setLoading(true);
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    streamAIResponse({
      query: q,
      signal: controller.signal,
      onDelta: (delta) => setResponse((c) => c + delta),
    })
      .then((fallback) => {
        if (!controller.signal.aborted && fallback) {
          setErrorMessage(fallback);
        }
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (!controller.signal.aborted) {
          setErrorMessage("Errore nella generazione della risposta.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  }, []);

  // Watch for the right conditions to stream
  useEffect(() => {
    if (!open || isListening || !query) return;
    startStream(query);
  }, [open, isListening, query, startStream]);

  const handleClose = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-x-0 bottom-0 z-[65] flex justify-center px-3 pb-3 pointer-events-none sm:pb-4">
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => {
              if (info.offset.y > 100) handleClose();
            }}
            className="pointer-events-auto flex w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-border bg-card/95 shadow-2xl backdrop-blur-xl"
            style={{ height: "40vh", maxHeight: "26rem" }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                </div>
                <span className="text-sm font-semibold text-foreground">Tecly AI</span>
              </div>
              <button onClick={handleClose} className="p-1.5 rounded-full hover:bg-muted transition-colors">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {/* Transcript */}
            {(isListening || transcript) && (
              <div className="px-5 pb-3">
                <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-2.5">
                  <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                    {isListening ? "Trascrizione live" : "Hai detto"}
                  </p>
                  <p className="mt-1 min-h-5 text-sm text-foreground line-clamp-2">
                    {transcript || "Parla pure..."}
                  </p>
                </div>
              </div>
            )}

            {/* Response area */}
            <div className="flex-1 overflow-y-auto px-5 pb-2">
              {!isListening && query && (
                <p className="mb-3 line-clamp-2 text-xs italic text-muted-foreground">"{query}"</p>
              )}

              {isListening ? (
                <div className="py-2 space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span>Sto ascoltando...</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Rilascia il tasto per eseguire il comando o inviare la domanda.
                  </p>
                </div>
              ) : errorMessage ? (
                <StreamingText text={errorMessage} loading={false} />
              ) : loading && !response ? (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">Sto pensando...</span>
                </div>
              ) : response || loading ? (
                <StreamingText text={response} loading={loading} />
              ) : !query ? (
                <p className="py-4 text-sm text-muted-foreground">Tieni premuto per parlare a Tecly AI.</p>
              ) : null}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
